import React from 'react';
import { Redirect } from 'react-router';

import { PageSize } from 'types/state';
import { useServices } from 'hooks/useServices';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServiceState } from 'hooks/useServiceState';
import { routes } from 'utils/routes';
import { Pagination } from 'design/pagination';
import { AdminContentLayout } from 'design/adminContentLayout';
import { AdminPageTitleLayout } from 'design/adminPageTitleLayout';
import { AdminPageHeadLayout } from 'design/adminPageHeadLayout';
import { PageSizeControl } from 'containers/PageSizeControl';
import { useTableSortingController } from 'hooks/useTableSortingController';
import { TableControlsLayout } from 'design/tableControlsLayout';
import { PromocodesTable } from 'containers/PromocodesTable/PromocodesTable';
import { Promocode, PromocodesTableSortableField } from 'types/state/promocode';
import { useSafeAsyncWrapper } from 'hooks/useSafeAsyncWrapper';
import { TextComponent } from 'design/text/textComponents';
import { SearchInputLayoutComponent } from 'design/searchInputLayout';
import { TextInputControlComponent } from 'design/textInputControl';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';
import { AdminNotificationPanel } from 'containers/AdminNotificationPanel';

type Props = {
  currentPage: number;
};

const MIN_SEARCH_VALUE_LENGTH_TO_SEARCH_BY = 2;
const DEFAULT_SORT_FIELD: PromocodesTableSortableField = 'id';
const DEFAULT_SORT_ASCENDING_FLAG = false;

export const PromocodesPageContent = (props: Props) => {
  const { currentPage } = props;

  const { sortBy, tableSorting: sorting } = useTableSortingController({
    initialField: DEFAULT_SORT_FIELD,
    initialAscending: DEFAULT_SORT_ASCENDING_FLAG,
  });

  const wrapAsyncSafely = useSafeAsyncWrapper();

  // Подключение интернационализации.
  const { promocodesPage: ownDictionary } = useIntlDictionary();

  // Состояние хранит количество страниц, доступных для
  // загрузки. Состояние хранится отдельно, чтобы пагинация
  // не пропадала при каждом новом вызове сервиса.
  const [totalPagesCount, setTotalPagesCount] = React.useState<null | number>(
    null
  );

  // Состояние хранит текстовое значение по которому будет
  // производиться поиск платежей.
  const [searchValue, setSearchValue] = React.useState('');

  // Для осуществления поиска по текстовому значению подходит
  // только строка определенной длины. Чтобы не провоцоировать
  // лишний вызов сервиса на получение данных страницы, значения
  // поисковой строки меньше определенной длины приводятся к
  // одинаковому значению.
  const searchBy =
    searchValue.length < MIN_SEARCH_VALUE_LENGTH_TO_SEARCH_BY
      ? null
      : searchValue;

  // Состояние хранит значение размера страницы.
  const [sizeValue, setSizeValue] = React.useState<PageSize>(20);

  // Обработчик изменившегося значения размера страницы.
  const onSizeValueChange = setSizeValue;

  // Подключение сервисов.
  const services = useServices();

  // Состояние и метод вызова сервиса на получение данных страницы.
  const [pageDataState, getPageDataByParams] = useServiceState(
    services.getPromocodesPageData
  );

  const getPageData = React.useCallback(() => {
    getPageDataByParams({
      page: currentPage,
      pageSize: sizeValue,
      sorting,
      searchBy: searchBy || undefined,
    });
  }, [currentPage, getPageDataByParams, searchBy, sizeValue, sorting]);

  // Эффект вызывает сервис на получение данных страницы после
  // монтирования и каждый раз, когда изменится один из параметров.
  // Используется useLayoutEfeect чтобы избежать мерцания индикатора
  // сортировки.
  React.useLayoutEffect(() => {
    getPageData();
  }, [getPageData]);

  // Эффект следит за состоянием сервиса на получение данных страницы
  // и обновляет значение доступного количества страниц.
  // Используется useLayoutEffect чтобы в случае несуществующей
  // страницы осуществить редирект без отрисовки пустых данных.
  React.useLayoutEffect(() => {
    if (pageDataState && pageDataState.resolvedWith) {
      setTotalPagesCount(pageDataState.resolvedWith.totalPagesCount);
    }
  }, [pageDataState]);

  // Список клиентов кэшируется в локальном состоянии.
  const [tableItems, setTableItems] = React.useState<null | Promocode[]>(null);

  // Кэшированный список клиентов инвалидируется при смене страницы.
  React.useEffect(() => {
    setTableItems(null);
  }, []);

  // При получении нового списка клиентов он заменяет собой кэшированный список.
  React.useEffect(() => {
    const tableItems =
      pageDataState &&
      pageDataState.resolvedWith &&
      pageDataState.resolvedWith.promocodes;

    if (tableItems) {
      setTableItems(tableItems);
    }
  }, [pageDataState]);

  const generatePromocode = React.useCallback(() => {
    const promise = services
      .generatePromoCode()
      .then((promocode) => {
        setTableItems((prevItems) => {
          if (prevItems) {
            return [promocode, ...prevItems];
          }

          return prevItems;
        });

        return promocode;
      })
      .then((promocode) => {
        document.getElementById(`${promocode.id}`)?.click();
      });

    wrapAsyncSafely(promise);
  }, [services, wrapAsyncSafely]);

  // В случае, когда выбранная страница не существует,
  // осуществляется редирект на последнюю доступную страницу.
  // Ситуация возникает, например, когда переключается размер страницы.
  if (totalPagesCount !== null && currentPage > totalPagesCount) {
    if (totalPagesCount > 0) {
      return <Redirect to={routes.promocodes(totalPagesCount)} />;
    } else if (currentPage > 1) {
      return <Redirect to={routes.promocodes(1)} />;
    }
  }

  const paginationBasePath = routes.promocodesRoot();

  return (
    <>
      <AdminContentLayout
        headNode={
          <AdminPageHeadLayout>
            <AdminNotificationPanel />
            <AdminPageTitleLayout
              textNode={
                <TextComponent size="mp">
                  {ownDictionary.titleText}
                </TextComponent>
              }
              actionNode={
                <ClickControlComponent onClick={generatePromocode}>
                  <TextComponent color="accent">
                    {ownDictionary.generatePromocodeActionText}
                  </TextComponent>
                </ClickControlComponent>
              }
            />
            <TableControlsLayout
              primaryControlsNode={
                <TextComponent size="s">
                  <SearchInputLayoutComponent
                    control={
                      <TextInputControlComponent
                        placeholder={ownDictionary.searchInputPlaceholderText}
                        value={searchValue}
                        onValueChange={setSearchValue}
                      />
                    }
                  />
                </TextComponent>
              }
              secondaryControlsNode={
                <>
                  {totalPagesCount !== null && (
                    <TextComponent size="s">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPagesCount}
                        basePath={paginationBasePath}
                      />
                    </TextComponent>
                  )}
                  <TextComponent size="s">
                    <PageSizeControl
                      value={sizeValue}
                      onValueChange={onSizeValueChange}
                    />
                  </TextComponent>
                </>
              }
            ></TableControlsLayout>
          </AdminPageHeadLayout>
        }
        bodyNode={
          tableItems !== null && (
            <PromocodesTable
              promocodes={tableItems}
              sortedBy={sorting.field}
              ascendingSort={sorting.ascending}
              onSortChange={sortBy}
              onItemClick={() => {}}
            />
          )
        }
      />
    </>
  );
};
